/**
 * Import d'un export **F-Secure** `.fsk` (F-Secure ID PROTECTION, ex-F-Secure
 * KEY : gestionnaire finlandais, ses utilisateurs doivent pouvoir partir avec
 * TOUT leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le fichier est un JSON `{ data: { <id>: entree } }` ; chaque
 * entree porte `type` (1 = identifiant, 2 = carte), `service` (le nom),
 * `username`, `password`, `url`, `notes`, `favorite` (horodatage, 0 = non),
 * `passwordList` (anciens mots de passe `{ password, changedate }`) et, pour
 * une carte, `creditNumber`, `creditCvv`, `creditExpiry` ; le titulaire est
 * alors `username` et le PIN `password`. Les metadonnees d'affichage et de
 * revision sont ecartees ; toute autre cle non vide devient un champ
 * personnalise, masque si son libelle designe un secret (aucune perte). Le
 * mot de passe est date de `passwordModifiedDate` (a defaut `modifiedDate`)
 * pour la Sante du coffre.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, type ImportedEntry } from './csv-import.js';
import type { PasswordHistoryEntry } from './password-history.js';
import { normalizeExpiry, normalizeTypedFields, serverHost } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Valeur brute d'un secret : jamais rognee, une espace peut en faire partie. */
function rawSecret(value: Json): string {
  return typeof value === 'string' ? value : asString(value);
}

const FSK_LOGIN = 1;
const FSK_CARD = 2;

/** Cles reprises par le lecteur, et metadonnees (couleur, style, revision, dates) sans valeur pour l'utilisateur. */
const KNOWN_KEYS = new Set([
  'type', 'service', 'username', 'password', 'url', 'notes', 'favorite', 'passwordList',
  'creditNumber', 'creditCvv', 'creditExpiry',
  'color', 'style', 'rev', 'createdDate', 'modifiedDate', 'passwordModifiedDate',
]);

/** Date d'un ancien mot de passe : secondes ou millisecondes UNIX, ou date lisible ; 0 si illisible. */
function changeDate(value: Json): number {
  const text = asString(value);
  if (/^\d+(\.\d+)?$/u.test(text)) {
    const n = Number(text);
    return n < 1e12 ? Math.round(n * 1000) : Math.round(n);
  }
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** `passwordList` -> historique chiffre de l'entree, plus recent d'abord ; le mot de passe courant n'y figure pas. */
function passwordHistory(value: Json, current: string): PasswordHistoryEntry[] {
  if (!Array.isArray(value)) return [];
  const history: PasswordHistoryEntry[] = [];
  for (const raw of value) {
    const item = asObject(raw);
    if (item === null) continue;
    const password = rawSecret(item['password']);
    if (password.length === 0 || password === current) continue;
    history.push({ password, changedAt: changeDate(item['changedate']) });
  }
  return history.sort((a, b) => b.changedAt - a.changedAt);
}

function parseFskEntry(entry: Record<string, Json>): ImportedEntry | null {
  const kind = Number(entry['type']);
  const service = asString(entry['service']);
  const username = asString(entry['username']);
  const password = rawSecret(entry['password']);
  const url = asString(entry['url']);
  // La note garde sa mise en forme (retraits, sauts de ligne), seuls ses bords sont rognes.
  const notes = typeof entry['notes'] === 'string' ? entry['notes'].trim() : asString(entry['notes']);
  const favorite = Number(entry['favorite']) > 0;
  const customFields: EntryCustomField[] = [];
  const isCard = kind === FSK_CARD;
  const fields: Record<string, string> = {};
  if (isCard) {
    fields['cardholder'] = username;
    fields['number'] = asString(entry['creditNumber']);
    fields['cvv'] = asString(entry['creditCvv']);
    fields['pin'] = asString(entry['password']);
    const expiry = asString(entry['creditExpiry']);
    const normalized = normalizeExpiry(expiry);
    if (normalized !== null) fields['expiry'] = normalized;
    else if (expiry.length > 0) customFields.push({ name: 'Expiration', value: expiry, hidden: false });
    if (url.length > 0) customFields.push({ name: 'Adresse', value: url, hidden: false });
  } else {
    // Un numero de carte porte par un identifiant reste un champ, toujours masque.
    for (const key of ['creditNumber', 'creditCvv', 'creditExpiry']) {
      const value = asString(entry[key]);
      if (value.length > 0) customFields.push({ name: key, value, hidden: key !== 'creditExpiry' });
    }
  }
  for (const [key, value] of Object.entries(entry)) {
    if (KNOWN_KEYS.has(key)) continue;
    const text = asObject(value) !== null || Array.isArray(value) ? JSON.stringify(value) : rawSecret(value);
    if (text.trim().length > 0) customFields.push(csvColumnField(key, text));
  }
  const history = isCard ? [] : passwordHistory(entry['passwordList'], password);
  const cardFields = isCard ? normalizeTypedFields('card', fields) : {};
  const hasCardData = Object.values(cardFields).some((value) => value.length > 0);
  if (
    service.length === 0 && username.length === 0 && password.length === 0 && url.length === 0 && notes.length === 0 &&
    customFields.length === 0 && history.length === 0 && !hasCardData
  ) {
    return null;
  }
  if (isCard) {
    return {
      type: 'card',
      name: service || cardFields['cardholder'] || 'Sans nom',
      username: '',
      password: '',
      uris: [],
      notes,
      totpSecret: '',
      favorite,
      fields: cardFields,
      ...(customFields.length > 0 ? { customFields } : {}),
    };
  }
  if (kind !== FSK_LOGIN) {
    // Type inconnu : garde en note, ses identifiant/mot de passe/adresse en champs (le mot de passe masque).
    const kept: EntryCustomField[] = [];
    if (username.length > 0) kept.push({ name: 'Identifiant', value: username, hidden: false });
    if (password.length > 0) kept.push({ name: 'Mot de passe', value: password, hidden: true });
    if (url.length > 0) kept.push({ name: 'Adresse', value: url, hidden: false });
    kept.push(...customFields);
    return {
      type: 'note',
      name: service || 'Sans nom',
      username: '',
      password: '',
      uris: [],
      notes,
      totpSecret: '',
      favorite,
      ...(kept.length > 0 ? { customFields: kept } : {}),
    };
  }
  // F-Secure date le mot de passe lui-meme (`passwordModifiedDate`) ; a defaut, la derniere modification de l'entree,
  // une borne haute -- jamais la creation (un mot de passe change depuis une fuite passerait pour compromis).
  const passwordChangedAt = password.length > 0 ? changeDate(entry['passwordModifiedDate']) || changeDate(entry['modifiedDate']) : 0;
  const host = url.length > 0 ? serverHost(url).replace(/^www\./u, '') : '';
  // Identifiant sans identifiant, mot de passe ni adresse : c'est une note.
  const isNote = username.length === 0 && password.length === 0 && url.length === 0 && history.length === 0;
  return {
    type: isNote ? 'note' : 'login',
    name: service || host || username || 'Sans nom',
    username,
    password,
    uris: url.length > 0 ? [url] : [],
    notes,
    totpSecret: '',
    favorite,
    ...(passwordChangedAt > 0 ? { passwordChangedAt } : {}),
    ...(history.length > 0 ? { passwordHistory: history } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
  };
}

/** Entree F-Secure : un objet portant un `type` numerique et la cle `service`. */
function isFskEntry(value: Json): boolean {
  const entry = asObject(value);
  return entry !== null && typeof entry['type'] === 'number' && 'service' in entry;
}

/**
 * Un export F-Secure se reconnait a son objet `data` (jamais une chaine, comme
 * l'export Bitwarden chiffre) dont TOUTES les valeurs sont des entrees F-Secure.
 */
export function isFSecureExport(data: Json): boolean {
  const file = asObject(data);
  const entries = asObject(file?.['data']);
  if (entries === null) return false;
  const values = Object.values(entries);
  return values.length > 0 && values.every(isFskEntry);
}

/**
 * Parse un export F-Secure `.fsk` : identifiants, cartes, anciens mots de
 * passe en historique. Les entrees entierement vides sont ecartees.
 */
export function parseFSecureExport(data: Json): ImportedEntry[] {
  const entries = asObject(asObject(data)?.['data']);
  if (entries === null) throw new Error('export F-Secure : structure inattendue');
  return Object.values(entries)
    .map(asObject)
    .filter((entry): entry is Record<string, Json> => entry !== null)
    .map(parseFskEntry)
    .filter((entry): entry is ImportedEntry => entry !== null);
}
